import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';
import { Button } from '../../PageStyles';
import { notify } from '../../../utils/notifications';
import { BaseModal } from '../../shared/BaseModal';
// Old modal components removed - now using BaseModal
const ComparisonTable = styled.div `
  padding: 32px;
  overflow-x: auto;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
  }

  th {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    color: var(--text-secondary);
    font-size: 14px;
  }

  tr:hover {
    background: var(--bg-tertiary);
  }

  .spec-label {
    font-weight: 600;
    color: var(--text-primary);
    min-width: 120px;
  }

  .best-value {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
    font-weight: 700;
  }

  .price-cell {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
  }
`;
const DiamondColumn = styled.div `
  text-align: center;
  padding: 12px;
  position: relative;

  .diamond-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .remove-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #ef4444;
    border: none;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: all 0.2s;

    &:hover {
      background: #dc2626;
      transform: scale(1.1);
    }
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 64px 32px;
  color: var(--text-tertiary);

  h3 {
    color: var(--text-primary);
    margin-bottom: 16px;
    font-size: 20px;
  }

  p {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;
export const ComparisonModal = ({ diamonds, isOpen, onClose, onRemove }) => {
    if (!isOpen)
        return null;
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    if (diamonds.length === 0) {
        return (_jsx(BaseModal, { isOpen: isOpen, onClose: onClose, title: "Compare Diamonds", maxWidth: "600px", children: _jsxs(EmptyState, { children: [_jsx("h3", { children: "No Diamonds Selected" }), _jsx("p", { children: "Select 2-4 diamonds from the inventory to compare them side-by-side" }), _jsx(Button, { onClick: onClose, children: "Browse Diamonds" })] }) }));
    }
    const getCaratWeight = (d) => d.caratWeight || parseFloat(d.carat || '0') || 0;
    const getPrice = (d) => d.priceUSD || 0;
    const getLab = (d) => d.lab || d.certification || 'Unknown';
    // Find best value (lowest price per carat)
    const pricesPerCarat = diamonds.map(d => getPrice(d) / getCaratWeight(d));
    const bestValueIndex = pricesPerCarat.indexOf(Math.min(...pricesPerCarat));
    return (_jsx(BaseModal, { isOpen: isOpen, onClose: onClose, title: `Compare Diamonds (${diamonds.length})`, maxWidth: "1400px", noPadding: true, children: _jsxs(ComparisonTable, { children: [_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "spec-label", children: "Specification" }), diamonds.map((diamond, index) => (_jsx("th", { children: _jsxs(DiamondColumn, { children: [_jsx("button", { className: "remove-btn", onClick: () => onRemove(diamond.id), title: "Remove from comparison", children: "\u00D7" }), _jsxs("div", { className: "diamond-title", children: ["Diamond ", index + 1] }), _jsx("div", { style: { fontSize: '12px', color: 'var(--text-tertiary)' }, children: diamond.stockNumber || diamond.id })] }) }, diamond.id)))] }) }), _jsxs("tbody", { children: [_jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Shape" }), diamonds.map(d => (_jsx("td", { children: d.shape }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Carat Weight" }), diamonds.map(d => (_jsxs("td", { children: [getCaratWeight(d).toFixed(2), "ct"] }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Color" }), diamonds.map(d => (_jsx("td", { children: d.color }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Clarity" }), diamonds.map(d => (_jsx("td", { children: d.clarity }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Cut" }), diamonds.map(d => (_jsx("td", { children: d.cut || 'N/A' }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Certification" }), diamonds.map(d => (_jsx("td", { children: getLab(d) }, d.id)))] }), diamonds.some(d => d.certificateNumber) && (_jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Cert Number" }), diamonds.map(d => (_jsx("td", { children: d.certificateNumber || 'N/A' }, d.id)))] })), _jsxs("tr", { style: { borderTop: '2px solid var(--border-color-hover)' }, children: [_jsx("td", { className: "spec-label", children: "Price" }), diamonds.map((d, index) => (_jsxs("td", { className: `price-cell ${index === bestValueIndex ? 'best-value' : ''}`, children: ["$", getPrice(d).toLocaleString(), index === bestValueIndex && (_jsxs("div", { style: { fontSize: '10px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx(FaCheckCircle, {}), " Best Value"] }))] }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Price per Carat" }), diamonds.map((d, index) => (_jsxs("td", { className: index === bestValueIndex ? 'best-value' : '', children: ["$", (getPrice(d) / getCaratWeight(d)).toLocaleString(undefined, { maximumFractionDigits: 0 }), "/ct"] }, d.id)))] }), _jsxs("tr", { children: [_jsx("td", { className: "spec-label", children: "Price Difference" }), diamonds.map(d => {
                                            const basePrice = getPrice(diamonds[0]);
                                            const thisPrice = getPrice(d);
                                            const diff = thisPrice - basePrice;
                                            return (_jsx("td", { style: { color: diff > 0 ? '#ef4444' : diff < 0 ? '#22c55e' : 'inherit' }, children: diff === 0 ? 'Base' : (diff > 0 ? '+' : '') + '$' + diff.toLocaleString() }, d.id));
                                        })] })] })] }), _jsxs("div", { style: { marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }, children: [_jsx(Button, { onClick: onClose, children: "Close Comparison" }), _jsx(Button, { variant: "secondary", onClick: () => {
                                // Future: Export comparison as PDF
                                notify.info('Export Coming Soon', 'Comparison export feature in development');
                            }, children: "Export Comparison" })] })] }) }));
};
